/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosCheckbox from '../../Form/Checkbox/ArgosCheckbox';
import ArgosFlexbox from '../../Containers/ArgosFlexbox';
import { ArgosSelectOption } from '../../Form/Select/types';
import {ICartesianPlotProps, PlotData} from '../constants';
import { idLineColorsMap, idToLabelLineTypesMap } from '../../../pages/vehiclePrograms/ItemDetails/Tabs/SensingSysPerfTab/constants';
import FormControl from '@mui/material/FormControl';
import Plot from "react-plotly.js"
import React, {useEffect, useState} from 'react';
import FormLabel from '@mui/material/FormLabel';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import VAR_COMMON_CONSTANTS from "../../../../constants/globalVariables";
import {calculateRangeWithBuffer, getMaxFinite} from "../../../../helpers/graphs";

/**
 * Renders a Cartesian Plot collapse section in the FullReport view.
 */
const CartesianPlot = (props: ICartesianPlotProps) => {
  const plotData = props.data
  const [lineOptions, setLineOptions] = useState<any[]>([])
  const [selectedLines, setSelectedLines] = useState<string[]>([])  // The line currently selected
  const [selectedPlotData, setSelectedPlotData] = useState<any>([])  // The data currrently shown on the plot
  const [configObject, setConfigObject] = useState<any>({})

  const [globalMin, setGlobalMin] = useState<number | null>(null)
  const [globalMax, setGlobalMax] = useState<number | null>(null)

  const handleChange = (event: any, lineId: string) => {
    const _selectedLines = [...selectedLines]
    const lineIndex = _selectedLines.indexOf(lineId)
    if (lineIndex != -1) {
      _selectedLines.splice(lineIndex, 1)
    } else {
      _selectedLines.push(lineId)
    }
    setSelectedLines(_selectedLines)
  }

  const getPlotRange = (absoluteMin: number, absoluteMax: number) => {
    if (globalMin == null || globalMin > absoluteMin) {
      setGlobalMin(absoluteMin)
    }
    if (globalMax == null || globalMax < absoluteMax) {
      setGlobalMax(absoluteMax)
    }
  }

  /**
   * Update selected plot data when selected objects and lines change
   */
  useEffect(() => {
    constructPlottingObject()
    constructConfigObject()
  }, [selectedLines])

  /**
   * Set line menu options
   */
  useEffect(() => {
    const _lineOptions: ArgosSelectOption[] = []
    plotData.plot.forEach((plot: PlotData) => {
      _lineOptions.push({id: plot.line, label: idToLabelLineTypesMap?.[plot.line] ?? plot.line})
    })
    setLineOptions(_lineOptions)
  }, [plotData])



  const constructPlottingObject = () => {
    let newPlotData: any = []
    let max:any=null
    selectedLines.forEach((line) => {
      const lineData = plotData.plot.find((element) => element.line === line)
      if (lineData !== undefined) {

        const maxX = getMaxFinite(lineData.xData);
        const maxY = getMaxFinite(lineData.yData);
        const absoluteMax = Math.max(maxX, maxY, 0);
        if (max == null || absoluteMax > max) max = absoluteMax;

        const _color = idLineColorsMap?.[lineData.line] ?? "black"
        newPlotData.push({
          x: lineData.xData,
          y: lineData.yData,
          type: lineData.graphType,
          mode: lineData.graphMode,
          marker: {color: _color}
        })
      }
    })
    setGlobalMin((-1) * max)
    setGlobalMax(max)
    setSelectedPlotData(newPlotData)
  }

  const useForceUpdate = () => {
    const [value, setValue] = useState(0)
    return () => setValue(value => value + 1)
  }

  const forceUpdate = useForceUpdate()
  const handleClick = () => {
    forceUpdate()
  }

  const constructConfigObject = () => {
    let _stuff = []
    const configObj = {
      name: 'Square Plot',
      icon:  {
        width: 80,
        height: 80,
        path: 'M78,22V79H21V22H78m9-9H12V88H87V13ZM68,46.22H31V54H68ZM53,32H45.22V69H53Z',
        transform: 'matrix(1 0 0 1 -10 -10)'

      },
      click:() =>handleClick(),
    }
    _stuff.push(configObj)
    setConfigObject({'modeBarButtonsToAdd' :_stuff})
  }

  const {minRange, maxRange} = calculateRangeWithBuffer(globalMin, globalMax)

  return(
    <ArgosFlexbox style={{justifyContent: "center", padding: 10, paddingTop: 0}}>
      <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
        <FormLabel component="legend">Select Lines</FormLabel>
        <FormGroup>
          {lineOptions.map((line: ArgosSelectOption) => (
            <FormControlLabel control={
                <ArgosCheckbox checked={selectedLines.indexOf(line.id) > -1} onChange={(e) => handleChange(e, line.id)}/>
              }
              label={line.label}
            />
          ))}
        </FormGroup>
      </FormControl>
      <Plot
        data={selectedPlotData}
        config={configObject}
        useResizeHandler={true}
        style={{height: 500, width: 500, paddingBottom: "3px"}}
        layout={{
          showlegend: false,
          title: plotData?.plotTitle ?? "",
          xaxis: {
            title: plotData?.xAxisLabel ?? "",
            rangemode: "tozero",
            nticks: 6,
            range: [minRange, maxRange]
          },
          yaxis: {
            title: plotData?.yAxisLabel ?? "",
            rangemode: "tozero",
            nticks: 6,
            range: [minRange, maxRange],
          },
        }}
      />
    </ArgosFlexbox>
  )
}

export default CartesianPlot
